"use client";

/**
 * Browser-side recovery from a stale Clerk session (see
 * lib/clerk-session-signal.ts). No Clerk SDK import: it talks to the
 * `window.Clerk` instance that clerk-js installs — loaded by ClerkScope inside
 * the app, and by the signed-in upkeep (components/auth/signed-in-clerk-upkeep.tsx)
 * on Clerk-free pages — so Clerk-free surfaces can use it without shipping
 * Clerk to anonymous visitors.
 */
import {
  CLERK_SESSION_STALE_CODE,
  CLERK_SIGNED_OUT_HINT_HEADER,
} from "@/lib/clerk-session-signal";

interface BrowserClerkSession {
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
}

interface BrowserClerk {
  loaded?: boolean;
  session?: BrowserClerkSession | null;
}

const CLERK_LOAD_WAIT_MS = 10_000;
const CLERK_LOAD_POLL_MS = 100;

/**
 * - `refreshed`: clerk-js minted a fresh session token (and wrote `__session`);
 *   the request can be sent again as the signed-in visitor.
 * - `signed_out`: clerk-js loaded and there is NO session — it ended or was
 *   revoked and only its cookies were left behind. The visitor really is
 *   signed out; send the request again with the signed-out hint so the server
 *   takes its anonymous path.
 * - `unavailable`: clerk-js did not load in time, or the token request failed.
 *   Nothing is known, so nothing is downgraded: the caller keeps the stale
 *   answer.
 */
export type ClerkSessionRefreshResult = "refreshed" | "signed_out" | "unavailable";

function loadedClerk(): BrowserClerk | null {
  const clerk = (window as { Clerk?: BrowserClerk }).Clerk;
  return clerk?.loaded ? clerk : null;
}

/** Wait (bounded) for clerk-js, then mint a fresh session token. */
export async function refreshClerkSession({
  timeoutMs = CLERK_LOAD_WAIT_MS,
}: { timeoutMs?: number } = {}): Promise<ClerkSessionRefreshResult> {
  if (typeof window === "undefined") return "unavailable";
  const deadline = Date.now() + timeoutMs;
  let clerk = loadedClerk();
  while (!clerk && Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, CLERK_LOAD_POLL_MS));
    clerk = loadedClerk();
  }
  if (!clerk) return "unavailable";
  if (!clerk.session) return "signed_out";
  try {
    if (await clerk.session.getToken({ skipCache: true })) return "refreshed";
    // No token for a session clerk-js still reports: not a confirmed sign-out.
    return clerk.session ? "unavailable" : "signed_out";
  } catch {
    return "unavailable";
  }
}

/** True for the 401 a route returns when the session token is stale. */
export async function isStaleClerkSessionResponse(
  response: Response,
): Promise<boolean> {
  if (response.status !== 401) return false;
  // Reading the code must never consume the caller's body, and must never
  // throw: anything that is not a readable copy is "not stale".
  try {
    const body = (await response.clone().json()) as { code?: unknown } | null;
    return body?.code === CLERK_SESSION_STALE_CODE;
  } catch {
    return false;
  }
}

/** `init` plus the signed-out hint header (lib/clerk-session-signal.ts). */
export function withClerkSignedOutHint(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set(CLERK_SIGNED_OUT_HINT_HEADER, "1");
  return { ...init, headers };
}

/**
 * `fetch`, plus one replay when the server says the session is stale: after a
 * session refresh (`refreshed`), or with the signed-out hint when clerk-js has
 * confirmed there is no session (`signed_out`). When clerk-js is unavailable
 * the stale 401 is returned to the caller. The request is replayed, so `init`
 * must be replayable (a string or FormData body, not a consumed stream).
 */
export async function fetchWithClerkSessionRefresh(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  if (!(await isStaleClerkSessionResponse(response))) return response;
  const refresh = await refreshClerkSession();
  if (refresh === "refreshed") return fetch(input, init);
  if (refresh === "signed_out") return fetch(input, withClerkSignedOutHint(init));
  return response;
}
